<script setup>
import { useAbility } from '@casl/vue'
import { cookieRef } from '@/@layouts/stores/config'
import RiskService from '@/services/quality/risk.service'
import { RiskLevel, riskLevelColor, riskLevelTitle } from '@/enums/quality/risk-level'
import { ReviewFrequency, reviewFrequencyTitle } from '@/enums/quality/review-frequency'
import RiskMatrixGrid from '@/views/quality/risk/RiskMatrixGrid.vue'

// VSkeletonLoader lives in Vuetify labs and is NOT auto-registered by this
// codebase's component resolver — an unimported use renders nothing, with no
// console error, so the loading state below silently disappears.
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'

// unplugin-vue-router derives the route name from the file path
// (`app-quality-risk-masters-matrix`) — see src/pages/app/quality/risk/[id].vue
// for the same convention (meta.name is a CASL key, not a route name).
definePage({
  meta: {
    subject: 'risk.menu',
    action: 'masters',
  },
})

const { can } = useAbility()
const unitId = cookieRef('selectedUnit')

const riskLevelOptions = Object.values(RiskLevel).map(value => ({ title: riskLevelTitle(value), value }))
const reviewFrequencyOptions = Object.values(ReviewFrequency).map(value => ({ title: reviewFrequencyTitle(value), value }))

// ── Fetch ────────────────────────────────────────────
const matrixCells = ref([])
const allLevels = ref([])
const isLoading = ref(false)

// Monotonic token guarding against out-of-order responses — same pattern as
// src/pages/app/quality/risk/index.vue.
let fetchToken = 0

const fetchMatrix = async () => {
  const token = ++fetchToken

  isLoading.value = true
  await RiskService.getMatrix({ unit_id: unitId.value }, data => {
    if (token !== fetchToken) return

    const payload = data?.data ?? data ?? {}

    matrixCells.value = payload.cells ?? []
    allLevels.value = payload.levels ?? []
    isLoading.value = false
  }, () => {
    if (token !== fetchToken) return
    isLoading.value = false
  })
}

fetchMatrix()

// RiskScoringService::matrix() returns levels for every scoring method
// (MATRIX_5X5 and RPN_FMEA), ordered by scoring_method — this editor owns
// only the 5x5 grid, so the level table and provenance banner both filter to
// MATRIX_5X5. The order here (LOW, MODERATE, HIGH, EXTREME) is fixed rather
// than whatever order the API returns, since it's a level ladder, not a list.
const levelOrder = ['LOW', 'MODERATE', 'HIGH', 'EXTREME']

const matrixLevels = computed(() => allLevels.value
  .filter(l => l.scoring_method === 'MATRIX_5X5')
  .slice()
  .sort((a, b) => levelOrder.indexOf(a.risk_level) - levelOrder.indexOf(b.risk_level)))

// Provenance lives on the level row, not the cell (RiskLevelConfig stores it
// once per level) — reconcileProvenance() sets the same source across every
// MATRIX_5X5 row, so the first one is representative.
const source = computed(() => matrixLevels.value[0]?.source ?? '—')

const cellFor = (severity, likelihood) => matrixCells.value.find(c => c.severity === severity && c.likelihood === likelihood)

const setCellLevel = (severity, likelihood, value) => {
  const cell = cellFor(severity, likelihood)
  if (cell) cell.risk_level = value
}

// ── Save ─────────────────────────────────────────────
const isSaving = ref(false)

const save = async () => {
  // All 25 cells are required by UpdateRiskMatrixRequest (size:25) — send the
  // full grid, not just the edited cell.
  const cellsPayload = matrixCells.value.map(c => ({
    severity: c.severity,
    likelihood: c.likelihood,
    score: c.score,
    risk_level: c.risk_level,
  }))

  // Only the MATRIX_5X5 levels are sent back — RiskScoringService::updateMatrix
  // matches each submitted level by unit + scoring_method + risk_level and
  // updates only those rows, so RPN_FMEA bands are left untouched by simply
  // not being in this payload.
  const levelsPayload = matrixLevels.value.map(l => ({
    scoring_method: l.scoring_method,
    risk_level: l.risk_level,
    label: l.label,
    descriptor: l.descriptor,
    color: l.color,
    review_frequency: l.review_frequency,
    min_score: l.min_score,
    max_score: l.max_score,
  }))

  isSaving.value = true

  await RiskService.updateMatrix({
    unit_id: unitId.value,
    cells: cellsPayload,
    levels: levelsPayload,
  }, () => {
    isSaving.value = false

    // Re-read rather than trust the local optimistic state — editing any cell
    // flips provenance to INTERNAL and restoring every cell restores
    // NHS_NPSA; the user must see that as a consequence of their edit, not an
    // assumption baked into the client.
    fetchMatrix()
  }, () => {
    isSaving.value = false
  })
}
</script>

<template>
  <section>
    <VAlert
      type="info"
      variant="tonal"
      class="mb-6"
    >
      Default derived from the UK NHS/NPSA 2008 risk matrix. Neither NABH nor
      JCI publishes a risk matrix, scoring bands or review cadences — NABH's
      Risk Management annexure is in the paid Guidebook. Current source:
      <strong>{{ source }}</strong>.
    </VAlert>

    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardItem>
        <template #title>
          <span class="text-h6 font-weight-medium">Scoring Matrix</span>
        </template>
        <template #subtitle>
          <span class="text-body-2 text-medium-emphasis">
            Rows: likelihood (descending). Columns: severity (ascending).
          </span>
        </template>
        <template #append>
          <div class="d-flex align-center gap-3">
            <VBtn
              v-if="can('configure', 'quality.risk')"
              color="primary"
              variant="flat"
              prepend-icon="tabler-device-floppy"
              :loading="isSaving"
              :disabled="isLoading"
              @click="save"
            >
              Save
            </VBtn>

            <IconBtn
              variant="tonal"
              class="rounded"
              :loading="isLoading"
              @click="fetchMatrix"
            >
              <VIcon icon="tabler-refresh" />
              <VTooltip activator="parent">
                Refresh
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VCardItem>

      <VDivider />

      <VCardText>
        <div
          v-if="isLoading"
          class="d-flex flex-column gap-2"
        >
          <VSkeletonLoader
            v-for="n in 5"
            :key="n"
            type="text"
            height="64"
          />
        </div>

        <RiskMatrixGrid
          v-else
          :cells="matrixCells"
          :levels="matrixLevels"
          mode="interactive"
          :level-options="riskLevelOptions"
          :disabled="!can('configure', 'quality.risk')"
          @update:cell="cell => setCellLevel(cell.severity, cell.likelihood, cell.value)"
        />
      </VCardText>
    </VCard>

    <VCard
      variant="flat"
      class="border"
    >
      <VCardItem>
        <template #title>
          <span class="text-h6 font-weight-medium">Risk Levels</span>
        </template>
        <template #subtitle>
          <span class="text-body-2 text-medium-emphasis">
            Label, descriptor, colour and review cadence per level
          </span>
        </template>
      </VCardItem>

      <VDivider />

      <VTable>
        <thead>
          <tr>
            <th>Level</th>
            <th>Label</th>
            <th>Descriptor</th>
            <th>Colour</th>
            <th>Review Frequency</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="level in matrixLevels"
            :key="level.risk_level"
          >
            <td>
              <VChip
                :color="riskLevelColor(level.risk_level)"
                size="small"
                label
              >
                {{ riskLevelTitle(level.risk_level) }}
              </VChip>
            </td>
            <td style="min-inline-size: 10rem;">
              <AppTextField
                v-model="level.label"
                density="compact"
                hide-details
                :disabled="!can('configure', 'quality.risk')"
              />
            </td>
            <td style="min-inline-size: 14rem;">
              <AppTextField
                v-model="level.descriptor"
                density="compact"
                hide-details
                :disabled="!can('configure', 'quality.risk')"
              />
            </td>
            <td style="min-inline-size: 10rem;">
              <div class="d-flex align-center gap-2">
                <VAvatar
                  size="20"
                  :color="level.color"
                />
                <AppTextField
                  v-model="level.color"
                  density="compact"
                  hide-details
                  :disabled="!can('configure', 'quality.risk')"
                />
              </div>
            </td>
            <td style="min-inline-size: 10rem;">
              <AppSelect
                v-model="level.review_frequency"
                :items="reviewFrequencyOptions"
                item-title="title"
                item-value="value"
                density="compact"
                hide-details
                :disabled="!can('configure', 'quality.risk')"
              />
            </td>
          </tr>
        </tbody>
      </VTable>
    </VCard>
  </section>
</template>
